import { ArrowRight, Loader2, Mail, Sparkles } from 'lucide-react'
import { ChangeEvent, FormEvent, useState } from 'react'

interface LoginFormViewProps {
  email: string
  validEmail: boolean
  isRequesting: boolean
  error: string | null
  onEmailChange: (e: ChangeEvent<HTMLInputElement>) => void
  onSubmit: (e: FormEvent) => void
}

export const LoginFormView: React.FC<LoginFormViewProps> = ({
  email,
  validEmail,
  isRequesting,
  error,
  onEmailChange,
  onSubmit,
}) => {
  const [isFocused, setIsFocused] = useState(false)

  return (
    <form className="login-form" onSubmit={onSubmit}>
      <div
        className={`input-wrapper ${isFocused ? 'focused' : ''} ${validEmail && email ? 'valid' : ''}`}
      >
        <div className="input-container">
          <div className="input-icon">
            <Mail className="icon" />
          </div>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            className="email-input"
            placeholder="admin@company.com"
            value={email}
            onChange={onEmailChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            disabled={isRequesting}
          />
          {validEmail && email && (
            <div className="validation-indicator">
              <Sparkles className="h-4 w-4" />
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="error-container">
          <div className="error-icon">!</div>
          <div className="error-content">
            <div className="error-title">Access Denied</div>
            <div className="error-message">{error}</div>
          </div>
        </div>
      )}

      <button type="submit" disabled={!validEmail || isRequesting} className="submit-button">
        <span className="button-content">
          {isRequesting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Sending code...</span>
            </>
          ) : (
            <>
              <span>Request Access Code</span>
              <ArrowRight className="h-4 w-4" />
            </>
          )}
        </span>
      </button>

      <style>{`
        /* Forced-dark login splash: zinc neutrals are intentionally literal
           (app theme tokens flip in light mode). Brand/status colors reference
           mode-invariant tokens: --primary, --accent, --success, --destructive. */
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        /* Input wrapper */
        .input-wrapper {
          position: relative;
        }

        .input-container {
          position: relative;
          display: flex;
          align-items: center;
          background: #0A0A0F;
          border: 1px solid #27272A;
          border-radius: 6px;
          transition: all 0.2s ease;
        }

        .input-wrapper.focused .input-container {
          border-color: hsl(var(--primary));
          box-shadow: 0 0 0 3px hsl(var(--primary) / 0.1);
        }

        .input-wrapper.valid .input-container {
          border-color: hsl(var(--success));
        }

        .input-wrapper.valid.focused .input-container {
          box-shadow: 0 0 0 3px hsl(var(--success) / 0.1);
        }

        .input-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 0.875rem;
          color: #71717A;
          transition: color 0.2s ease;
        }

        .input-wrapper.focused .input-icon {
          color: hsl(var(--primary));
        }

        .input-wrapper.valid .input-icon {
          color: hsl(var(--success));
        }

        .icon {
          width: 18px;
          height: 18px;
        }

        .email-input {
          flex: 1;
          padding: 0.75rem 0.875rem 0.75rem 0;
          background: transparent !important;
          background-color: transparent !important;
          border: none;
          outline: none;
          color: #FAFAFA;
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-size: 1rem; /* 16px to prevent iOS auto-zoom */
          -webkit-text-fill-color: #FAFAFA;
        }

        @media (min-width: 640px) {
          .email-input {
            font-size: 0.875rem;
          }
        }

        .email-input:-webkit-autofill,
        .email-input:-webkit-autofill:hover,
        .email-input:-webkit-autofill:focus,
        .email-input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 1000px #0A0A0F inset !important;
          -webkit-text-fill-color: #FAFAFA !important;
          background-color: #0A0A0F !important;
          transition: background-color 5000s ease-in-out 0s;
          caret-color: hsl(var(--primary));
        }

        .email-input::placeholder {
          color: #52525B;
          -webkit-text-fill-color: #52525B;
        }

        .email-input:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .validation-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          padding-right: 0.875rem;
          color: hsl(var(--success));
        }

        /* Error container */
        .error-container {
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          padding: 0.75rem;
          background: hsl(var(--destructive) / 0.1);
          border: 1px solid hsl(var(--destructive) / 0.25);
          border-radius: 6px;
        }

        .error-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: hsl(var(--destructive) / 0.15);
          border-radius: 50%;
          color: hsl(var(--destructive));
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-weight: 600;
          font-size: 0.7rem;
          flex-shrink: 0;
        }

        .error-content {
          flex: 1;
        }

        .error-title {
          color: hsl(var(--destructive));
          font-family: 'Geist Mono', ui-monospace, monospace;
          font-weight: 500;
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.125rem;
        }

        .error-message {
          color: #A1A1AA;
          font-size: 0.8rem;
        }

        /* Submit button */
        .submit-button {
          position: relative;
          padding: 0.75rem 1.25rem;
          background: linear-gradient(135deg, hsl(var(--accent)) 0%, color-mix(in srgb, hsl(var(--accent)) 80%, black) 100%);
          border: none;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s ease;
          box-shadow: 0 0 20px hsl(var(--accent) / 0.2);
        }

        .submit-button:not(:disabled):hover {
          transform: translateY(-1px);
          box-shadow: 0 0 30px hsl(var(--accent) / 0.3);
        }

        .submit-button:not(:disabled):active {
          transform: translateY(0);
        }

        .submit-button:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }

        .button-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          color: hsl(var(--accent-foreground));
          font-family: 'Satoshi', system-ui, sans-serif;
          font-weight: 600;
          font-size: 0.875rem;
        }
      `}</style>
    </form>
  )
}
